import styled from 'styled-components';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import Button from 'design-system/src/components/Button/Button';
import type { ReferralFormData } from '../../../types.ts';
import { FormLabel } from '../../FormLabel.tsx';
import { Input } from '../../Input.tsx';
import { Dropdown, DropdownContainer } from '../../Dropdown.tsx.tsx';
import { FormContainer } from '../../legacySignup/FormContainer/FormContainer.tsx';
import { requiredErrorString, requiredErrorMessage } from '../../../utils.tsx';
import { getRoleOptions } from '../../legacySignup/utils.tsx';
import { ControllerWrapper } from '../../legacySignup/ControllerWrapper.tsx';
import { EMAIL_VALIDATION_REGEX } from '../../../constants.ts';

const StyledReferralForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

type ReferralFormProps = {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  errors: FieldErrors<ReferralFormData>;
  control: Control<ReferralFormData>;
  isSubmitting?: boolean;
};

/**
 * Form collecting referral information from a provider to refer another practice to Tandem.
 */
export const ReferralForm = ({ onSubmit, errors, control, isSubmitting }: ReferralFormProps) => {
  return (
    <FormContainer
      title="Refer a practice"
      subtitle="Thank you for referring someone to Tandem! Please fill out this information, and you will be CC'd on the outreach email. "
      showExtraContent={true}
    >
      <StyledReferralForm onSubmit={onSubmit}>
        <ControllerWrapper>
          <Controller
            control={control}
            name="referrer_name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input label="Your Name" autoComplete="name" hasError={!!errors.referrer_name} {...field} />
            )}
          />
          {errors.referrer_name && requiredErrorMessage}
        </ControllerWrapper>

        <ControllerWrapper>
          <Controller
            control={control}
            name="referrer_email"
            rules={{
              required: { value: true, message: requiredErrorString },
              pattern: {
                value: EMAIL_VALIDATION_REGEX,
                message: 'Invalid email',
              },
            }}
            render={({ field }) => (
              <Input label="Your Email" autoComplete="email" hasError={!!errors.referrer_email} {...field} />
            )}
          />
          {errors.referrer_email && <ErrorMessage>{errors.referrer_email.message}</ErrorMessage>}
        </ControllerWrapper>

        <ControllerWrapper>
          <Controller
            control={control}
            name="referred_practice_name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Referral Practice Name"
                autoComplete="off"
                hasError={!!errors.referred_practice_name}
                {...field}
              />
            )}
          />
          {errors.referred_practice_name && requiredErrorMessage}
        </ControllerWrapper>

        <ControllerWrapper>
          <Controller
            control={control}
            name="referred_contact_name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Referral Point of Contact Name"
                autoComplete="off"
                hasError={!!errors.referred_contact_name}
                {...field}
              />
            )}
          />
          {errors.referred_contact_name && requiredErrorMessage}
        </ControllerWrapper>

        <Controller
          name="referred_contact_role"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DropdownContainer>
              <FormLabel id="role-label">Referral Point of Contact Role</FormLabel>
              <Dropdown labelId="role-label" $hasError={!!errors.referred_contact_role} {...field}>
                {getRoleOptions()}
              </Dropdown>
              {errors.referred_contact_role && requiredErrorMessage}
            </DropdownContainer>
          )}
        />

        <ControllerWrapper>
          <Controller
            control={control}
            name="referred_contact_email"
            rules={{
              required: { value: true, message: requiredErrorString },
              pattern: {
                value: EMAIL_VALIDATION_REGEX,
                message: 'Invalid email',
              },
            }}
            render={({ field }) => (
              <Input
                label="Referral Point of Contact Email"
                autoComplete="off"
                hasError={!!errors.referred_contact_email}
                {...field}
              />
            )}
          />
          {errors.referred_contact_email && <ErrorMessage>{errors.referred_contact_email.message}</ErrorMessage>}
        </ControllerWrapper>

        <Button type="submit" width="full" size="lg" isLoading={isSubmitting}>
          Continue
        </Button>
      </StyledReferralForm>
    </FormContainer>
  );
};
